import { useI18n } from "vue-i18n";
import { computed, toValue, type MaybeRefOrGetter } from "vue";
import type { ChartItem, ItemForChart, SelectableChartItem, SelectableChartItemType } from "./ChartItem";
import type { SimulationItem } from "../simulation/SimulationItem";

interface GetTitleContext {
  singleSimulation?: boolean;
  selectedSimulations: SimulationItem[];
  selectedItems: SelectableChartItem<SelectableChartItemType>[];
  availableItems: {
    [type in SelectableChartItemType]: Record<string, ItemForChart<type>>;
  };
  dataToggles: ChartItem["dataToggles"];
}

export function useChartTitle(customTitle: MaybeRefOrGetter<string | undefined | null>, titleContext: MaybeRefOrGetter<GetTitleContext>) {
  const { t, locale } = useI18n();

  // =======
  // TITLE

  const title = computed(() => {
    return formattedTitle.value.map((t) => t.text).join("");
  });

  const formattedTitle = computed((): { text: string; style?: string }[] => {
    const customTitleValue = toValue(customTitle);
    if (customTitleValue) {
      return [{ text: customTitleValue }];
    }
    return generatedTitleParts.value.length > 0
      ? generatedTitleParts.value
      : [ { text: t(`objectNames.chart`) } ];
  });

  const generatedTitleParts = computed((): { text: string; style?: string }[] => {
    const context = toValue(titleContext);
    return defaultGetTitle({
      ...context
    });
  });

  /**
   * Return the title of a simulation,
   * pretty detailed.
   */
  function defaultGetTitle(context: GetTitleContext) {
    const titleParts = [];
    const localeConcat = new Intl.ListFormat(locale.value, {
      style: "long",
      type: "conjunction"
    });

    const {
      selectedSimulations,
      selectedItems,
      availableItems,
      dataToggles,
    } = context;

    const allSimulationsActive = dataToggles.all.simulation && !context.singleSimulation;

    if (allSimulationsActive) {
      titleParts.push({ text: t("labels.allSimulations") });
    }
    if (selectedItems.length > 0) {
      if (titleParts.length > 0) titleParts.push({ text: " – " });
      if (selectedItems.length == 1) {
        const item = availableItems[selectedItems[0].type][selectedItems[0].id];
        titleParts.push({
          text: "title" in item ? item.title : "---",
          style: "font-weight: 700"
        });
      } else {
        const types = selectedItems.reduce((types, item) => {
          if (!types.includes(item.type)) types.push(item.type);
          return types;
        }, [] as SelectableChartItemType[]);

        titleParts.push({
          text: localeConcat.format(
            types.map((type): string => {
              if (type == "target" && dataToggles.all.target) {
                return t("labels.allTargets");
              }
              if (type == "resource" && dataToggles.all.resource) {
                return t("labels.allResources");
              }
              const itemsOfType = selectedItems.filter((i) => i.type == type);
              if (itemsOfType.length == 1) {
                const item =
                  availableItems[itemsOfType[0].type][itemsOfType[0].id];
                return "title" in item
                  ? item?.title
                  : t(`objectNames.${type}`, itemsOfType.length);
              } else {
                return t(`objectNames.${type}`, itemsOfType.length);
              }
              return "";
            })
          )
        });
      }
    }
    if (!allSimulationsActive) {
      if (titleParts.length > 0) {
        titleParts.push({
          text: ` ${t("words.in")} `
        });
      }

      if (selectedSimulations.length == 1) {
        titleParts.push({
          text: selectedSimulations[0].title,
          style: "font-weight: 600; fill: var(--color-text-medium); color: var(--color-text-medium)"
        });
      } else if (selectedSimulations.length > 1) {
        titleParts.push({
          text: `${selectedSimulations.length} ${t(
            "objectNames.simulation",
            selectedSimulations.length
          )}`
        });
      }
    }
    return titleParts;
  }

  return {
    title,
    formattedTitle,
    generatedTitleParts,
  }
}
